/**
 * phi-scrub.ts — PHI scrubbing for Sentry events (runtime-agnostic).
 *
 * Pure functions with a single dependency (@nrx/audit/phi-fields), so this module
 * is safe to import in BOTH Node (services) and browser (React apps) bundles: it
 * pulls no @sentry/*, no aws-sdk, and no node:async_hooks. The Node entry
 * (sentry.ts) and the browser entry (@nrx/error-tracking-web) both wire these
 * scrubbers into their Sentry `beforeSend` hook, so PHI never leaves either
 * boundary into the Sentry SaaS.
 */
import { PHI_FIELD_DENYLIST } from '@nrx/audit/phi-fields';
const phiSet = new Set(PHI_FIELD_DENYLIST);
/**
 * Regex patterns for PHI that may appear inline in exception value strings.
 * These patterns match common PHI formats in free-text error messages.
 *
 * MBI note: The strict CMS MBI format has specific character class rules per position.
 * For exception scrubbing we use a broader 11-character pattern starting with a non-zero
 * digit followed by alphanumerics, which reliably matches MBI-formatted identifiers
 * in error message context while avoiding false positives on common numeric tokens.
 */
const PHI_STRING_PATTERNS = [
    // MBI (Medicare Beneficiary Identifier): 11 alphanumeric chars, starts with [1-9],
    // followed by exactly 10 uppercase alpha/digit characters. This broad pattern covers
    // both strict CMS format and test/example MBIs that appear in exception messages.
    /\b[1-9][A-Z0-9]{10}\b/g,
    // SSN: 000-00-0000
    /\b\d{3}-\d{2}-\d{4}\b/g,
    // Date of birth patterns: YYYY-MM-DD or MM/DD/YYYY
    /\b(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])\b/g,
    /\b(?:0[1-9]|1[0-2])\/(?:0[1-9]|[12]\d|3[01])\/(?:19|20)\d{2}\b/g,
    // Invite token: 64-char lowercase hex string (PHI-adjacent credential).
    // A raw invite token grants access to an enrollment session — treat as PHI.
    /\b[0-9a-f]{64}\b/gi,
];
/**
 * Scrubs PHI patterns from a free-text string (exception value).
 */
function scrubString(value) {
    let scrubbed = value;
    for (const pattern of PHI_STRING_PATTERNS) {
        // Reset lastIndex for global regexes between calls
        pattern.lastIndex = 0;
        scrubbed = scrubbed.replace(pattern, '[REDACTED]');
    }
    return scrubbed;
}
/**
 * Scrubs top-level PHI fields from an object: values whose keys match the
 * denylist are replaced with '[REDACTED]'; the key is preserved.
 */
export function scrubObject(obj) {
    if (obj === undefined || obj === null)
        return obj;
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        result[key] = phiSet.has(key) ? '[REDACTED]' : value;
    }
    return result;
}
/**
 * Scrubs PHI from Sentry exception values (free-text error message strings).
 * Applies regex-based PHI pattern matching to each exception value string.
 */
function scrubExceptionValues(exception) {
    if (exception === undefined || exception === null)
        return exception;
    if (!Array.isArray(exception.values))
        return exception;
    return {
        ...exception,
        values: exception.values.map((exVal) => {
            if (exVal.value === undefined || exVal.value === null || typeof exVal.value !== 'string') {
                return exVal;
            }
            return {
                ...exVal,
                value: scrubString(exVal.value),
            };
        }),
    };
}
/**
 * Scrubs PHI fields from all Sentry event surfaces:
 * event.extra, event.user, event.contexts, and event.exception value strings.
 * Values whose keys match PHI_FIELD_DENYLIST are replaced with '[REDACTED]'.
 * Exception value strings are scrubbed for inline PHI patterns (MBI, SSN, DOB).
 * Returns null if the event itself is null/undefined (Sentry drops the event).
 */
export function scrubPhiFromSentryEvent(event) {
    if (event === null || event === undefined)
        return null;
    return {
        ...event,
        ...(event.extra !== undefined && { extra: scrubObject(event.extra) }),
        ...(event.user !== undefined && {
            user: scrubObject(event.user),
        }),
        ...(event.contexts !== undefined && {
            contexts: Object.fromEntries(Object.entries(event.contexts).map(([k, v]) => [k, scrubObject(v) ?? {}])),
        }),
        ...(event.exception !== undefined && {
            exception: scrubExceptionValues(event.exception),
        }),
    };
}
//# sourceMappingURL=phi-scrub.js.map